// Single source of truth for each route's <title>, description and link preview.
//
// It is read twice, so keep it free of JSX, browser globals and the "@/" alias:
//
//   - by the app, where <RouteSeo /> keeps the head in step with client-side
//     navigation (src/seo/RouteSeo.jsx);
//   - by scripts/prerender.cjs at build time, which writes these tags into a
//     real HTML file per route. That build step is the point of this file:
//     LinkedIn, WhatsApp and X run no JavaScript, so without it every link we
//     share unfurls as the homepage whatever page it actually points at.
//
// A route added to App.js needs an entry in STATIC below, or metaForPath reads
// it as an address with no page behind it and hands back the 404 metadata.
//
// Prices are deliberately absent from every description here. The pricing tiers
// come from the API at runtime, so a number written into a meta tag would go
// stale silently and we would be advertising a price the page doesn't show.

import { DOCS, DOC_GROUPS } from "../content/docs";
import { GUIDES } from "../content/guides";
import { LEGAL_PAGES, LEGAL_NAV } from "../content/legal";
import { pages } from "../data/pages";
import { compare } from "../data/compare";

export const SITE = {
  origin: "https://ownpbx.in",
  name: "OwnPBX",
  legalName: "Oneway Networks Technology Pvt. Ltd.",
  email: "ops@ownpbx.in",
  image: "https://ownpbx.in/brand/og-image.png",
};

const HOME_TITLE =
  "OwnPBX — A phone system that talks back | AI Voice Agent + Cloud PBX";

// Hand-written for the pages worth ranking. Each title leads with the words a
// buyer actually searches for, not with the brand.
const STATIC = {
  "/": {
    title: HOME_TITLE,
    description:
      "OwnPBX is a cloud, multi-tenant business phone system with an AI voice agent built into the calling infrastructure. Inbound & outbound AI calls in English, Hindi & Hinglish.",
    type: "website",
  },
  "/pricing": {
    title: "Pricing — cloud PBX and AI voice agents | OwnPBX",
    description:
      "Per-extension pricing for the PBX platform, with or without calling included. Bring your own carrier and pay for the software only. AI voice agents are priced separately.",
  },
  "/demo": {
    title: "Book a demo of OwnPBX | OwnPBX",
    description:
      "See the console, the call routing and an AI voice agent on a live call. Tell us how your calls run today and we will set the demo up around it.",
  },
  "/ai/demos": {
    title: "Talk to a live AI voice agent | OwnPBX",
    description:
      "Speak to AI agents for support, sales, appointments, collections and real estate right now, in the browser. Real product, fictional companies — no booking needed.",
  },
  "/numbers": {
    title: "Indian city-code phone numbers for business | OwnPBX",
    description:
      "City-code VoIP numbers your customers recognise as local — Mumbai, Delhi, Bengaluru, Chennai, Hyderabad, Pune, Kolkata and more. Route them to extensions, IVR, queues or an AI agent.",
  },
  "/pbx/analytics": {
    title: "Call reporting and PBX analytics | OwnPBX",
    description:
      "Call volume and answer rates per tenant, who called which number and how long they waited, queue SLA, voicemail by mailbox — per call, not a monthly PDF.",
  },
  "/about": {
    title: "About OwnPBX | OwnPBX",
    description:
      "We build where the audio actually flows, not as a bolt-on widget: self-hosted carrier-grade infrastructure, and English, Hindi and Hinglish from day one.",
  },
  "/careers": {
    title: "Careers — build voice infrastructure | OwnPBX",
    description:
      "Open roles in voice infrastructure, speech and LLM engineering, full-stack React and FastAPI, and reseller partnerships. Small team, big surface area.",
  },
  "/signup": {
    title: "Get started with OwnPBX | OwnPBX",
    description:
      "Tell us which you need — the business phone system, an autonomous AI voice agent, or both — and we will get your account set up.",
  },
  "/docs": {
    title: "Documentation — set up your phone system | OwnPBX",
    description:
      "How to set up extensions, connect desk phones and softphones, route numbers through IVRs and queues, add SIP trunks, build AI agents, and use the API.",
  },
  "/guides": {
    title: "Guides — cloud phone systems, AI voice agents and Indian calling rules | OwnPBX",
    description:
      "Plain explanations of how business calling works: cloud PBX and SIP basics, what an AI voice agent can and cannot do, and the rules that apply to automated calls in India.",
  },
  "/compare": {
    title: "Compare OwnPBX with Indian cloud telephony providers | OwnPBX",
    description:
      "Honest comparisons against the categories we compete with: bundled minutes versus bring-your-own-carrier, AI sold separately versus built in, English-first voice bots versus Hindi and Hinglish — including where they suit you better.",
  },
};

// The /compare/* pages. Titles lead with the words someone types when they are
// already shopping; descriptions say what the page actually argues, including
// the part where another provider wins. Written by hand rather than derived
// from each page's lead, because the lead is a sentence to read on the page and
// a description is a sentence to read in a result list.
const COMPARE_META = {
  "compare/knowlarity-alternative": {
    title: "Knowlarity alternative — cloud PBX with AI voice agents built in | OwnPBX",
    description:
      "An India-first alternative: keep your own carrier and numbers and pay for software, with AI voice agents inside the phone system in English, Hindi and Hinglish. Includes where an established provider suits you better.",
  },
  "compare/exotel-alternative": {
    title: "Exotel alternative — a cloud phone system, not a calling API | OwnPBX",
    description:
      "OwnPBX is a phone system your operations team configures in a browser, with AI voice agents inside it. Honest on what a communications platform does better: SMS, WhatsApp, OTP and global numbers, which we do not offer.",
  },
  "compare/myoperator-alternative": {
    title: "MyOperator alternative — cloud PBX with real extensions and AI agents | OwnPBX",
    description:
      "Desk phones, softphones, IVR, queues, recording and multi-tenant reporting, with an AI agent inside the routing. We have no mobile app, so this page says plainly when a lighter call-management tool is the better buy.",
  },
  "compare/acefone-alternative": {
    title: "Servetel and Acefone alternative — bring your own carrier | OwnPBX",
    description:
      "Buy the phone system and AI voice agents as software and keep your own carrier, numbers and negotiated rates — or buy calling from us. Where a single bundled vendor is still the better choice is on the page too.",
  },
  "compare/callhippo-alternative": {
    title: "CallHippo alternative for India — Hindi AI voice agents | OwnPBX",
    description:
      "India-first instead of numbers in dozens of countries: city-code numbers, IVR and queues, and AI agents built on Indian speech models. If you need global number coverage, we say so and point you elsewhere.",
  },
  "compare/bring-your-own-carrier-cloud-pbx": {
    title: "Cloud PBX with bring your own carrier (BYOC) in India | OwnPBX",
    description:
      "Keep your SIP trunk, numbers and negotiated rates and pay for the phone system as software, with per-call cost in the console. What BYOC involves, and when bundled minutes are the better deal.",
  },
  "compare/hindi-ai-voice-agent": {
    title: "Hindi and Hinglish AI voice agent for business calls | OwnPBX",
    description:
      "An AI agent built on Indian speech models that handles Hindi, Hinglish and mid-sentence code-switching, lives inside the phone system and transfers to a human with context. Plus when a person or an IVR is still better.",
  },
};

// Keyword-bearing titles for the 13 feature and partner pages. The description
// falls back to each page's own lead paragraph in src/data/pages.js, so it
// always matches what the page says.
const SUB_TITLES = {
  "ai/overview": "AI voice agent for business calls",
  "ai/inbound": "AI receptionist that answers every inbound call",
  "ai/outbound": "AI outbound calling on your own caller ID",
  "ai/web-chat": "AI web chat with the same agent as your phones",
  "ai/multilingual": "Hindi and Hinglish AI voice agent",
  "ai/analytics": "AI call analytics — sentiment, intent and topics",
  "pbx/multi-tenant": "Multi-tenant cloud PBX for resellers",
  "pbx/features": "Cloud PBX features — IVR, queues, recording, reports",
  "pbx/softphone": "Browser phone and softphone for your team",
  "pbx/security": "PBX security — toll fraud and SIP protection",
  "partners/reseller": "Become an OwnPBX reseller",
  "partners/white-label": "White-label cloud PBX and AI voice agents",
  "partners/affiliate": "OwnPBX affiliate programme",
};

const clip = (s, n = 320) => {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  return t.length <= n ? t : `${t.slice(0, n - 1).trimEnd()}…`;
};

const legalPath = (slug) =>
  (LEGAL_NAV.find((n) => n.slug === slug) || {}).to || `/${slug}`;

// path -> { title, description, theme, type }
export const ROUTE_META = (() => {
  const map = { ...STATIC };

  for (const [slug, page] of Object.entries(pages)) {
    const name = SUB_TITLES[slug] || String(page.title || "").replace(/\.$/, "");
    map[`/${slug}`] = {
      title: `${name} | ${SITE.name}`,
      description: clip(page.lead),
    };
  }

  // A compare page without an entry above would silently inherit the homepage's
  // tags, which is the exact failure this file exists to prevent — so fall back
  // to the page's own words instead, and make the gap obvious while developing.
  for (const [slug, page] of Object.entries(compare)) {
    const hand = COMPARE_META[slug];
    if (!hand && process.env.NODE_ENV !== "production") {
      console.warn(`meta: /${slug} has no hand-written title in COMPARE_META`);
    }
    map[`/${slug}`] = {
      title: hand ? hand.title : `${String(page.title).replace(/[.?]$/, "")} | ${SITE.name}`,
      description: hand ? hand.description : clip(page.lead),
    };
  }

  for (const [slug, doc] of Object.entries(DOCS)) {
    map[`/docs/${slug}`] = {
      title: `${doc.title} — OwnPBX documentation`,
      description: clip(doc.summary),
      type: "article",
    };
  }

  // Guides. The description is the guide's own summary, which is written to be
  // read in a result list, so there is no second copy to drift from it. Unlike
  // the docs the title is not suffixed with the brand — these pages are landed
  // on from search by people who do not know us yet, and the question they
  // typed is worth more of the 60 characters than our name is.
  for (const [slug, guide] of Object.entries(GUIDES)) {
    map[`/guides/${slug}`] = {
      title: `${guide.title} | ${SITE.name}`,
      description: clip(guide.summary, 300),
      type: "article",
    };
  }

  for (const [slug, page] of Object.entries(LEGAL_PAGES)) {
    map[legalPath(slug)] = {
      title: `${page.title} | ${SITE.name}`,
      description: clip(page.intro, 200),
    };
  }

  return map;
})();

export const DEFAULT_META = STATIC["/"];

// Deliberately not a ROUTE_META entry. Everything in there is prerendered and
// written into the sitemap, and offering Google a 404 page as a real URL is the
// problem this fixes rather than the fix. scripts/prerender.cjs writes it to
// build/404.html, which netlify.toml serves with a 404 status.
export const NOT_FOUND_META = {
  title: "Page not found — OwnPBX",
  description:
    "That address has no page behind it. Links to pricing, the AI voice agent, the cloud PBX platform, the guides and the documentation.",
  type: "website",
};

// Trailing slashes and casing differ between what a crawler asks for and what
// the router matched, so normalise before looking a path up.
export const metaForPath = (pathname) => {
  const p = `/${String(pathname || "/").trim().replace(/^\/+|\/+$/g, "")}`.toLowerCase();
  return ROUTE_META[p === "/" ? "/" : p] || NOT_FOUND_META;
};

// With a trailing slash, because that is the URL Netlify actually serves: its
// Pretty URLs setting answers /pricing with a 301 to /pricing/. Declaring the
// slashless form made every page canonical to a URL that redirects straight
// back to the page, which leaves Google resolving a conflict instead of being
// told the answer. The sitemap and og:url are built from this too, so all three
// agree. metaForPath strips the slash again before looking a route up.
export const canonicalFor = (pathname) => {
  const p = `/${String(pathname || "/").replace(/^\/+|\/+$/g, "")}`;
  return `${SITE.origin}${p === "/" ? "/" : `${p}/`}`;
};

export const ALL_PATHS = Object.keys(ROUTE_META);

export { DOCS, DOC_GROUPS, LEGAL_PAGES, pages, compare, GUIDES };
